import { DateTime } from 'luxon';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useServerTranslation } from 'hooks/useServerTranslation';
import useNotificationBar from '../../hooks/useNotificationBar';
import {
    SelfRegisterCreateCustomerSchemaType,
    useCreateSelfRegisterCustomerSchema,
} from './schemas/useCreateSelfRegisterCustomerSchema';
import { GraphQlExceptionsEnum, useCreateSelfRegisterCustomerMutation } from '../../generated/graphql';
import { SelfRegisterCustomerFormProps } from './SelfRegisterCustomerFormProps';

export function useSelfRegisterCustomerFormHook(consultantId: SelfRegisterCustomerFormProps['consultantId']) {
    const { t } = useServerTranslation();
    const { showSuccess, showError } = useNotificationBar();
    const customerSchema = useCreateSelfRegisterCustomerSchema();

    const [createCustomer, { data, loading }] = useCreateSelfRegisterCustomerMutation({
        onError: (error) => {
            if (error.message === GraphQlExceptionsEnum.CustomerEmailAlreadyExistsException) {
                showError(t('profile.register-account-form.account-already-exists-error-message'));
            } else {
                showError(t('profile.register-account-form.general-error'));
            }
        },
        onCompleted: () => {
            showSuccess(t('profile.register-account-form.success-message'));
        },
    });

    const methods = useForm<SelfRegisterCreateCustomerSchemaType>({
        resolver: zodResolver(customerSchema),
        mode: 'onTouched',
    });

    const { handleSubmit, setError } = methods;

    const onSubmit = async (createCustomerForm: SelfRegisterCreateCustomerSchemaType) => {
        const {
            password_again,
            user_agreement,
            ...rest
        } = createCustomerForm;

        if (password_again !== rest.password) {
            setError('password_again', { message: t('user.create-password.passwords-dont-match') });

            return;
        }

        const user = {
            ...rest,
            date_of_birth: DateTime.fromISO(rest.date_of_birth ?? '').toISODate(),
        };

        await createCustomer({ variables: { user, consultantId } });
    };

    return {
        methods,
        loading,
        formComplete: data?.createSelfRegisterCustomer?.success === true,
        handleSubmit: handleSubmit(onSubmit),
    };
}
